import React, { useState, useEffect, Suspense, lazy, useRef, forwardRef, useImperativeHandle, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, Mail, Phone, MapPin, Building2, Globe, ZoomIn, ZoomOut, RotateCcw, Copy, Check, X } from "lucide-react";
import { SectionHeading } from "../components/shared/SectionHeading";
import { BRAND } from "../lib/data";
import { INDUSTRY_SOLUTIONS } from "../lib/industries";
import { submitContact } from "../lib/api";
import { useTheme } from "next-themes";
import DottedMap from "dotted-map";

const FloatingKnot = lazy(() => import("../components/three/FloatingKnot"));

const emptyForm = {
    name: "",
    email: "",
    company: "",
    phone: "",
    industry: "",
    subject: "",
    message: "",
};

export default function Contact() {
    const [searchParams] = useSearchParams();
    const [form, setForm] = useState(emptyForm);
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    // Prefill from URL query params on first mount only.
    // Deliberately NOT re-running on searchParams change to avoid
    // overwriting a user's typing mid-session.
    useEffect(() => {
        const subject = searchParams.get("subject") || "";
        const industry = searchParams.get("industry") || "";
        const practice = searchParams.get("practice") || "";
        const message = practice
            ? `Hi Netfotech team, I'd like to speak with the ${practice} practice lead about a potential engagement.\n\nContext:\n- `
            : "";
        if (subject || industry || message) {
            setForm((f) => ({
                ...f,
                subject: subject || f.subject,
                industry: industry || f.industry,
                message: message || f.message,
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onChange = (e) =>
        setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Please fill name, email and message.");
            return;
        }
        setLoading(true);
        try {
            const res = await submitContact(form);
            toast.success("Message received.", {
                description: res?.email_sent
                    ? `We've emailed our team at ${BRAND.email}.`
                    : "We'll reach back within 1 business day.",
            });
            setSubmitted(true);
            setForm(emptyForm);
        } catch (err) {
            toast.error("Couldn't send.", {
                description:
                    err?.response?.data?.detail ||
                    "Please try again in a moment.",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <main data-testid="page-contact" className="relative pt-32 md:pt-40">
            <section className="relative pb-16 overflow-hidden">
                <div className="absolute inset-0 -z-10 grid-lines opacity-40" />
                <div className="absolute right-0 top-0 -z-10 h-[360px] w-[360px] hidden md:block opacity-55">
                    <Suspense fallback={null}>
                        <FloatingKnot color="#7FD03A" accent="#1E88E5" speed={0.18} />
                    </Suspense>
                </div>
                <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10">
                    <SectionHeading
                        eyebrow="Contact"
                        title="Let's design what's next."
                        subtitle="Tell us about your ambition. A senior practice lead will respond within one business day."
                    />
                </div>
            </section>

            <section className="relative py-10">
                <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
                    {/* Form */}
                    <div className="lg:col-span-8">
                        <motion.form
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            onSubmit={onSubmit}
                            data-testid="contact-form"
                            className="glass rounded-3xl border border-white/5 p-8 md:p-10"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <Field label="Full name" name="name" value={form.name} onChange={onChange} required />
                                <Field label="Work email" name="email" type="email" value={form.email} onChange={onChange} required />
                                <Field label="Company" name="company" value={form.company} onChange={onChange} />
                                <Field label="Phone" name="phone" value={form.phone} onChange={onChange} />
                                <div>
                                    <label className="text-[12px] uppercase tracking-[0.2em] text-zinc-500">
                                        Industry
                                    </label>
                                    <select
                                        name="industry"
                                        value={form.industry}
                                        onChange={onChange}
                                        data-testid="contact-field-industry"
                                        className="mt-2 w-full rounded-xl border border-white/10 bg-ink-800/80 px-4 py-3 text-[14.5px] text-white focus:border-brand-blue focus:ring-1 focus:ring-brand-blue outline-none"
                                    >
                                        <option value="">Select an industry</option>
                                        {INDUSTRY_SOLUTIONS.map((ind) => (
                                            <option key={ind.id} value={ind.name}>
                                                {ind.name}
                                            </option>
                                        ))}
                                        <option value="Other">Other</option>
                                    </select>
                                </div>
                                <Field label="Subject" name="subject" value={form.subject} onChange={onChange} placeholder="How can we help?" />
                                <div className="md:col-span-2">
                                    <label className="text-[12px] uppercase tracking-[0.2em] text-zinc-500">
                                        Message <span className="text-brand-blue">*</span>
                                    </label>
                                    <textarea
                                        name="message"
                                        rows={5}
                                        value={form.message}
                                        onChange={onChange}
                                        required
                                        data-testid="contact-field-message"
                                        placeholder="Briefly describe your goals, timelines and any context we should know."
                                        className="mt-2 w-full rounded-xl border border-white/10 bg-ink-800/80 px-4 py-3 text-[14.5px] text-white placeholder:text-zinc-500 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue outline-none resize-none"
                                    />
                                </div>
                            </div>
                            <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
                                <div className="text-[12.5px] text-zinc-500">
                                    By submitting, you agree to be contacted about your inquiry.
                                </div>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    data-testid="contact-submit-button"
                                    className="btn-glow relative inline-flex items-center gap-2 rounded-full bg-brand-linear px-7 py-3.5 text-[15px] font-medium text-white hover:shadow-glow-brand transition-all disabled:opacity-60"
                                >
                                    {loading ? "Sending…" : "Send message"}
                                    <ArrowUpRight className="h-4 w-4" />
                                </button>
                            </div>

                            {submitted && (
                                <div
                                    className="mt-6 rounded-2xl border border-brand-green/30 bg-brand-green/5 p-5 text-[14px] text-brand-green"
                                    data-testid="contact-success"
                                >
                                    Thanks your message has been logged and forwarded to our team.
                                </div>
                            )}
                        </motion.form>
                    </div>

                    {/* Side */}
                    <aside className="lg:col-span-4 space-y-5">
                        <ContactTile
                            icon={Mail}
                            title="Email us"
                            value={BRAND.email}
                            href={`mailto:${BRAND.email}`}
                            testId="contact-tile-email"
                        />
                        <ContactTile
                            icon={Phone}
                            title="Call us"
                            value={BRAND.phone}
                            href={`tel:${BRAND.phone.replace(/\s|-/g, "")}`}
                            testId="contact-tile-phone"
                        />
                        <ContactTile
                            icon={Building2}
                            title="Head Office"
                            value={BRAND.headOffice}
                            testId="contact-tile-hq"
                        />
                        {/* <div
                            data-testid="contact-tile-branches"
                            className="glass rounded-3xl border border-white/5 p-6 brand-border-hover"
                        >
                            <div className="flex items-start gap-4">
                                <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white/5 border border-white/10 shrink-0">
                                    <MapPin className="h-5 w-5 text-brand-green" />
                                </div>
                                <div>
                                    <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                                        Branch Offices
                                    </div>
                                    <div className="mt-2 flex flex-wrap gap-1.5">
                                        {BRAND.branchOffices.map((b) => (
                                            <span
                                                key={b}
                                                className="text-[11.5px] rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-zinc-300"
                                            >
                                                {b}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div> */}
                        <ContactTile
                            icon={Globe}
                            title="Website"
                            value={BRAND.website}
                            href={`https://${BRAND.website}`}
                            testId="contact-tile-website"
                        />
                    </aside>
                </div>
            </section>

            {/* OUR PRESENCE SECTION */}
            <PresenceSection />

            <div className="h-24" />
        </main>
    );
}

const PUNE_LAT_LNG = { lat: 18.5204, lng: 73.8567 };

const REGION_CONNECTIONS = {
    india: [
        { start: PUNE_LAT_LNG, end: { lat: 12.9716, lng: 77.5946 } }, // Bengaluru
        { start: PUNE_LAT_LNG, end: { lat: 28.4595, lng: 77.0266 } }, // Gurugram
        { start: PUNE_LAT_LNG, end: { lat: 22.5726, lng: 88.3639 } }, // Kolkata
        { start: PUNE_LAT_LNG, end: { lat: 30.3165, lng: 78.0322 } }  // Dehradun
    ],
    "north-america": [
        { start: PUNE_LAT_LNG, end: { lat: 44.7973, lng: -106.9562 } } // Sheridan
    ],
    emea: [
        { start: PUNE_LAT_LNG, end: { lat: 51.5074, lng: -0.1278 } } // London
    ]
};

const REGIONS = [
    {
        id: "india",
        name: "INDIA",
        offices: [
            {
                name: "NETFOTECH SOLUTIONS (Headquarters)",
                address: "World Trade Center, Tower 2, Level 9, Kharadi, Pune 411014, Maharashtra",
                lat: 18.5204, lng: 73.8567
            },
            {
                name: "NETFOTECH SOLUTIONS",
                address: "RMZ Ecoworld Rd, Adarsh Palm Retreat, Bellandur, Bengaluru, Karnataka 560103",
                lat: 12.9716, lng: 77.5946
            },
            {
                name: "NETFOTECH SOLUTIONS",
                address: "Tower A, 5th Floor, Building 10, DLF Cyber City, DLF Phase 2, Sector 24, Gurugram, Haryana 122002",
                lat: 28.4595, lng: 77.0266
            },
            {
                name: "NETFOTECH SOLUTIONS",
                address: "3rd Floor, Plot No Y5, EP Block, Sector V, Bidhannagar, Kolkata, West Bengal 700091",
                lat: 22.5726, lng: 88.3639
            },
            {
                name: "NETFOTECH SOLUTIONS",
                address: "Ground and 1st floor, 412/28/114, Jakhan, Dehradun, Uttarakhand 248001",
                lat: 30.3165, lng: 78.0322
            }
        ]
    },
    {
        id: "north-america",
        name: "NORTH AMERICA",
        offices: [
            {
                name: "NETFOTECH SOLUTIONS - LLC",
                address: "30 N Gould St. Ste. 4000, Sheridan, Wyoming 82801, USA",
                lat: 44.7973, lng: -106.9562
            }
        ]
    },
    {
        id: "emea",
        name: "EMEA",
        offices: [
            {
                name: "NETFOTECH SOLUTIONS",
                address: "12 Steward Street, The Steward Building, London, E1 6FQ",
                lat: 51.5074, lng: -0.1278
            }
        ]
    }
];

const LOCATIONS = [
    { lat: 18.5204, lng: 73.8567, name: "Pune (Headquarters)", region: "india" },
    { lat: 12.9716, lng: 77.5946, name: "Bengaluru Office", region: "india" },
    { lat: 28.4595, lng: 77.0266, name: "Gurugram Office", region: "india" },
    { lat: 22.5726, lng: 88.3639, name: "Kolkata Office", region: "india" },
    { lat: 30.3165, lng: 78.0322, name: "Dehradun Office", region: "india" },
    { lat: 44.7973, lng: -106.9562, name: "Sheridan Office (USA)", region: "north-america" },
    { lat: 51.5074, lng: -0.1278, name: "London Office (UK)", region: "emea" }
];

// All connections from all regions combined
const ALL_CONNECTIONS = Object.values(REGION_CONNECTIONS).flat();

const getLocationName = (lat, lng) => {
    const loc = LOCATIONS.find(
        (l) => Math.abs(l.lat - lat) < 0.1 && Math.abs(l.lng - lng) < 0.1
    );
    return loc ? loc.name : "Office Location";
};

const getLocationRegion = (lat, lng) => {
    const loc = LOCATIONS.find(
        (l) => Math.abs(l.lat - lat) < 0.1 && Math.abs(l.lng - lng) < 0.1
    );
    return loc ? loc.region : null;
}; const WorldMap = forwardRef(function WorldMap({ dots = [], activeRegion = "india", lineColor = "#7FD03A", hoveredOffice = null, selectedOffice = null, onPinClick = null }, ref) {
    const map = new DottedMap({ height: 100, grid: "diagonal" });
    const [hoveredLocation, setHoveredLocation] = useState(null);

    let theme = "dark";
    try {
        const themeContext = useTheme();
        if (themeContext && themeContext.theme) {
            theme = themeContext.theme;
        }
    } catch (e) {
        // Fallback
    }

    const mapDotColor = theme === "dark" ? "rgba(255, 255, 255, 0.25)" : "rgba(0, 0, 0, 0.15)";

    const svgMap = map.getSVG({
        radius: 0.14,
        color: mapDotColor,
        shape: "circle",
        backgroundColor: "transparent",
    });

    const projectPoint = useCallback((lat, lng) => {
        const pin = map.getPin({ lat, lng });
        return pin ? { x: pin.x, y: pin.y } : { x: 0, y: 0 };
    }, [map]);

    // Highlight pin when hoveredOffice changes
    useEffect(() => {
        if (hoveredOffice && hoveredOffice.lat != null && hoveredOffice.lng != null) {
            const pt = projectPoint(hoveredOffice.lat, hoveredOffice.lng);
            setHoveredLocation({ name: getLocationName(hoveredOffice.lat, hoveredOffice.lng), x: pt.x, y: pt.y });
        } else {
            setHoveredLocation(null);
        }
    }, [hoveredOffice, projectPoint]);

    // Build unique points from ALL locations for interactive overlays
    const allPoints = [];
    LOCATIONS.forEach((loc) => {
        const pt = projectPoint(loc.lat, loc.lng);
        let finalX = pt.x;
        let finalY = pt.y;

        // If there's already a point at the exact same or extremely close position (within 1.8 grid units)
        // offset it slightly so both are clickable and visible!
        let isOverlap = true;
        let offsetIndex = 0;
        while (isOverlap) {
            isOverlap = false;
            for (const existing of allPoints) {
                const dist = Math.hypot(existing.x - finalX, existing.y - finalY);
                if (dist < 1.8) {
                    isOverlap = true;
                    break;
                }
            }
            if (isOverlap) {
                offsetIndex++;
                const angle = offsetIndex * 2.3;
                const radius = 1.8;
                finalX = pt.x + Math.cos(angle) * radius;
                finalY = pt.y + Math.sin(angle) * radius;
            }
        }
        allPoints.push({ x: finalX, y: finalY, name: loc.name, region: loc.region, lat: loc.lat, lng: loc.lng });
    });

    // Calculate viewbox for zooming/panning
    const zoomFactor = selectedOffice ? 2.2 : 1;
    const w = map.width / zoomFactor;
    const h = map.height / zoomFactor;
    let minX = 0;
    let minY = 0;
    if (selectedOffice) {
        const pt = projectPoint(selectedOffice.lat, selectedOffice.lng);
        if (pt && pt.x !== 0 && pt.y !== 0) {
            minX = Math.max(0, Math.min(map.width - w, pt.x - w / 2));
            minY = Math.max(0, Math.min(map.height - h, pt.y - h / 2));
        }
    }
    const viewBox = `${minX} ${minY} ${w} ${h}`;

    return (
        <div className="w-full aspect-[2/1] relative font-sans overflow-hidden select-none border border-white/5 rounded-2xl bg-ink-950/10">
            <div className="w-full h-full relative">
                <motion.svg
                    viewBox={viewBox}
                    animate={{ viewBox }}
                    transition={{ type: "spring", stiffness: 80, damping: 18 }}
                    className="w-full h-full absolute inset-0 select-none"
                    style={{ pointerEvents: "auto" }}
                >
                    {/* SVG map background, embedded inside the SVG to zoom/pan along with viewBox */}
                    <image
                        href={`data:image/svg+xml;utf8,${encodeURIComponent(svgMap)}`}
                        x="0"
                        y="0"
                        width={map.width}
                        height={map.height}
                        className="opacity-80 pointer-events-none select-none"
                    />

                    {/* ALL location dots — active region highlighted, others dimmed */}
                    {allPoints.map((pt, i) => {
                        const isActive = pt.region === activeRegion;
                        const isHovered = hoveredLocation && hoveredLocation.name === pt.name;
                        const dotColor = isActive ? lineColor : "rgba(127, 208, 58, 0.25)";
                        const dotR = isHovered ? 2.0 : isActive ? 1.1 : 0.7;
                        return (
                            <g key={`loc-dot-${i}`} style={{ pointerEvents: "none" }}>
                                <circle cx={pt.x} cy={pt.y} r={dotR} fill={dotColor} style={{ transition: "r 150ms ease" }} />
                                {isActive && (
                                    <circle cx={pt.x} cy={pt.y} r="1.1" fill={lineColor} opacity="0.5">
                                        <animate attributeName="r" from="1.1" to="3.5" dur="2s" begin={`${0.15 * i}s`} repeatCount="indefinite" />
                                        <animate attributeName="opacity" from="0.5" to="0" dur="2s" begin={`${0.15 * i}s`} repeatCount="indefinite" />
                                    </circle>
                                )}
                            </g>
                        );
                    })}

                    {/* Interactive overlay targets for hover/clicks. Using opacity 0 fill for bulletproof browser click detection */}
                    {allPoints.map((pt, idx) => (
                        <circle
                            key={`target-${idx}`}
                            cx={pt.x}
                            cy={pt.y}
                            r="3.5"
                            fill="white"
                            opacity="0"
                            style={{ pointerEvents: "auto", cursor: "pointer" }}
                            onMouseEnter={() => setHoveredLocation({ name: pt.name, x: pt.x, y: pt.y })}
                            onMouseLeave={() => setHoveredLocation(null)}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (onPinClick) {
                                    onPinClick(pt);
                                }
                            }}
                        />
                    ))}
                </motion.svg>

                {/* Tooltip Overlay */}
                {hoveredLocation && (
                    <div
                        style={{
                            position: "absolute",
                            left: `${((hoveredLocation.x - minX) / w) * 100}%`,
                            top: `${((hoveredLocation.y - minY) / h) * 100}%`,
                            transform: `translate(-50%, -125%)`,
                            transformOrigin: "bottom center",
                            pointerEvents: "none",
                        }}
                        className="z-50 px-3 py-1.5 rounded-lg bg-ink-900/95 backdrop-blur-md border border-brand-green/30 text-white text-[12px] font-sans font-medium whitespace-nowrap shadow-xl flex items-center gap-1.5 transition-all duration-150"
                    >
                        <span className="w-1.5 h-1.5 rounded-full bg-brand-green animate-pulse" />
                        {hoveredLocation.name}
                    </div>
                )}
            </div>
        </div>
    );
});

function PresenceSection() {
    const [activeRegion, setActiveRegion] = useState("india");
    const [hoveredIdx, setHoveredIdx] = useState(null);
    const [selectedIdx, setSelectedIdx] = useState(null);
    const [copied, setCopied] = useState(false);
    const currentRegion = REGIONS.find((r) => r.id === activeRegion) || REGIONS[0];
    const mapRef = useRef(null);

    const handlePinClick = (pin) => {
        // 1. Switch region if the clicked pin is in a different region
        if (pin.region !== activeRegion) {
            setActiveRegion(pin.region);
        }

        // 2. Find the index of the office in the target region
        const targetRegion = REGIONS.find((r) => r.id === pin.region);
        if (targetRegion) {
            const officeIdx = targetRegion.offices.findIndex(
                (o) => Math.abs(o.lat - pin.lat) < 0.1 && Math.abs(o.lng - pin.lng) < 0.1
            );
            if (officeIdx !== -1) {
                // Set the highlighted office index
                setSelectedIdx(officeIdx);
                setCopied(false);
            }
        }
    };

    const handleCopy = (address) => {
        navigator.clipboard.writeText(address);
        setCopied(true);
        toast.success("Address copied to clipboard!");
        setTimeout(() => setCopied(false), 2000);
    };

    const handleClosePanel = () => {
        setSelectedIdx(null);
        setCopied(false);
    };

    const activeOfficeIdx = hoveredIdx !== null ? hoveredIdx : selectedIdx;
    const hoveredOffice = activeOfficeIdx !== null ? currentRegion.offices[activeOfficeIdx] : null;
    const selectedOffice = selectedIdx !== null ? currentRegion.offices[selectedIdx] : null;

    return (
        <section className="relative py-8 md:py-12 border-t border-white/5 bg-ink-950/20">
            <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10">
                <div className="mb-6 flex justify-center w-full">
                    <SectionHeading
                        align="center"
                        title={<>Global Reach. <span className="text-gradient-brand">Local Expertise.</span></>}
                        subtitle="Wherever you operate, our engineering talent and practice leads are positioned to accelerate your business goals."
                    />
                </div>

                {/* Region Tabs */}
                <div className="flex flex-wrap justify-center gap-2 md:gap-4 border-b border-white/10 pb-2 mb-6 overflow-x-auto scrollbar-none">
                    {REGIONS.map((region) => (
                        <button
                            key={region.id}
                            onClick={() => {
                                setActiveRegion(region.id);
                                setHoveredIdx(null);
                                setSelectedIdx(null);
                                setCopied(false);
                            }}
                            className={`text-[13px] md:text-[14px] uppercase tracking-[0.2em] font-semibold py-2 px-4 transition-all duration-300 relative ${activeRegion === region.id ? "text-brand-green" : "text-zinc-500 hover:text-white"
                                }`}
                        >
                            {region.name}
                            {activeRegion === region.id && (
                                <motion.div
                                    layoutId="activeRegionLine"
                                    className="absolute bottom-0 left-0 right-0 h-[2px] bg-brand-green"
                                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                                />
                            )}
                        </button>
                    ))}
                </div>

                {/* Stack layout: 100% Width Map with right side panel overlay */}
                <div className="space-y-10">
                    {/* Map Container */}
                    <div id="presence-world-map" className="w-full glass rounded-3xl border border-white/5 p-4 md:p-6 flex items-center justify-center relative overflow-hidden bg-ink-900/50 min-h-[350px] md:min-h-[550px]">
                        {/* Map wrapper container that shrinks when panel is active */}
                        <div className={`w-full h-full transition-all duration-500 ease-in-out ${selectedOffice ? "lg:pr-96" : "lg:pr-0"}`}>
                            <WorldMap ref={mapRef} activeRegion={activeRegion} dots={ALL_CONNECTIONS} lineColor="#7FD03A" hoveredOffice={hoveredOffice} selectedOffice={selectedOffice} onPinClick={handlePinClick} />
                        </div>

                        {/* Slide-out Panel from the right */}
                        <AnimatePresence>
                            {selectedOffice && (
                                <motion.div
                                    initial={{ x: "100%" }}
                                    animate={{ x: 0 }}
                                    exit={{ x: "100%" }}
                                    transition={{ type: "spring", damping: 25, stiffness: 200 }}
                                    className="absolute right-0 top-0 bottom-0 z-20 w-full sm:w-80 md:w-96 bg-ink-950/95 backdrop-blur-md border-l border-white/10 p-6 flex flex-col justify-between shadow-2xl"
                                >
                                    <div>
                                        <div className="flex items-center justify-between border-b border-white/10 pb-4 mb-4">
                                            <span className="text-[11px] uppercase tracking-[0.2em] text-brand-green font-semibold">Office Details</span>
                                            <button onClick={handleClosePanel} className="p-1.5 rounded-full hover:bg-white/10 text-zinc-400 hover:text-white transition-colors">
                                                <X className="h-4 w-4" />
                                            </button>
                                        </div>
                                        <h4 className="font-display font-semibold text-white text-[18px] tracking-tight flex items-start gap-2">
                                            <Building2 className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                                            {selectedOffice.name}
                                        </h4>
                                        <div className="mt-5 p-4 rounded-xl bg-white/[0.02] border border-white/5 relative group/address">
                                            <p className="text-[14px] text-zinc-300 leading-relaxed font-sans pr-8">
                                                {selectedOffice.address}
                                            </p>
                                            <button
                                                onClick={() => handleCopy(selectedOffice.address)}
                                                className="absolute right-3 top-3 p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-400 hover:text-white transition-all flex items-center justify-center"
                                                title="Copy Address"
                                            >
                                                {copied ? <Check className="h-4 w-4 text-brand-green" /> : <Copy className="h-4 w-4" />}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
                                        <a
                                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedOffice.address)}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 px-4 py-2.5 text-[14px] font-medium text-white transition-all"
                                        >
                                            <MapPin className="h-4 w-4 text-brand-blue" />
                                            Open in Google Maps
                                            <ArrowUpRight className="h-3 w-3 opacity-60" />
                                        </a>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </section>
    );
}

function Field({ label, name, value, onChange, type = "text", required, placeholder }) {
    return (
        <div>
            <label className="text-[12px] uppercase tracking-[0.2em] text-zinc-500">
                {label}{required && <span className="text-brand-blue"> *</span>}
            </label>
            <input
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                required={required}
                placeholder={placeholder || label}
                data-testid={`contact-field-${name}`}
                className="mt-2 w-full rounded-xl border border-white/10 bg-ink-800/80 px-4 py-3 text-[14.5px] text-white placeholder:text-zinc-500 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue outline-none"
            />
        </div>
    );
}

function ContactTile({ icon: Icon, title, value, href, testId }) {
    const content = (
        <div className="glass rounded-3xl border border-white/5 p-6 flex items-start gap-4 brand-border-hover">
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white/5 border border-white/10 shrink-0">
                <Icon className="h-5 w-5 text-brand-green" />
            </div>
            <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">{title}</div>
                <div className="mt-1 text-[15px] text-white">{value}</div>
            </div>
        </div>
    );
    return href ? (
        <a href={href} data-testid={testId} className="block">
            {content}
        </a>
    ) : (
        <div data-testid={testId}>{content}</div>
    );
}
